import { KIND_LABEL } from "./invoices";
import type { Kunde } from "./types";
import type {
  AbschlagAbsetzung,
  InterneKalkulation,
  InvoiceEditorOptions,
  InvoiceKind,
  TextSnippet,
  WebInvoiceDraft,
  WebInvoiceLine,
  WebInvoiceLineKind,
} from "./invoices";

export const localIso = (date = new Date()) => {
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 10);
};

export const addDays = (iso: string, days: number) => {
  const [year, month, day] = iso.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  date.setDate(date.getDate() + days);
  return localIso(date);
};

/** Kalendertage zwischen zwei ISO-Daten — Grundlage der Zahlungsziel-Kopplung. */
export const calendarDaysBetween = (fromIso: string, toIso: string) => {
  const [fy, fm, fd] = fromIso.split("-").map(Number);
  const [ty, tm, td] = toIso.split("-").map(Number);
  if (!fy || !ty) return 0;
  const diff = Date.UTC(ty, tm - 1, td) - Date.UTC(fy, fm - 1, fd);
  return Math.max(0, Math.round(diff / 86_400_000));
};

const neueZeilenId = () => (
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `line-${Date.now()}-${Math.random().toString(36).slice(2)}`
);

/**
 * Der vorgeschlagene Dokumenttitel: Belegart, dahinter das Projekt.
 *
 * Seit 07.09.2026 steht die Belegart IM Titel, nicht mehr davor — die Vorlage
 * setzt nichts mehr hinzu, damit der Nutzer die ganze Zeile bestimmen kann.
 * Die Belegnummer gehört bewusst nicht hinein: sie steht im Metablock des
 * Dokuments, und im Titel würde sie jeden selbst geschriebenen Text veralten
 * lassen, sobald sie sich beim Festschreiben ändert.
 */
// „Angebotsbestätigung" steht mit drin, damit sie beim Umstellen der Belegart
// als Vorschlag gilt und nicht als halbes „Angebot" + „sbestätigung" zerfällt.
const KIND_LABELS = ["Angebot", "Angebotsbestätigung", "Rechnung", "Abschlagsrechnung", "Schlussrechnung"];

/** Was hinter der Belegart im Titel steht — für den Vorschlag der neuen Art. */
const projektTeil = (titel: string) => {
  const rest = titel.trim();
  for (const label of [...KIND_LABELS].sort((a, b) => b.length - a.length)) {
    if (rest.toLowerCase().startsWith(label.toLowerCase())) {
      return rest.slice(label.length).trim();
    }
  }
  return rest;
};

export const titelVorschlag = (kind: InvoiceKind, projekt: string) => (
  [KIND_LABEL[kind], projekt.trim()].filter(Boolean).join(" ")
);

export const newInvoiceLine = (lineKind: WebInvoiceLineKind = "position"): WebInvoiceLine => ({
  id: neueZeilenId(),
  line_kind: lineKind,
  title: "",
  description: "",
  quantity: 1,
  unit_label: lineKind === "section" ? "pauschal" : "Stück",
  unit_code: "C62",
  unit_price_net: 0,
  vat_rate_percent: 19,
});

/**
 * Jede Zeile eines geladenen Belegs bekommt eine eigene `id`.
 *
 * Der Editor führt genau eine offene Zeile, und zwar über ihre `id`. Zeilen
 * ohne id gelten damit alle als dieselbe: ein Klick öffnete neun Formulare
 * gleichzeitig (Volker, 07.09.2026 — Belege aus dem KI-Import trugen
 * `"id": ""`). Doppelte ids haben denselben Effekt, deshalb wird auch die
 * zweite Verwendung derselben id neu vergeben.
 *
 * Geheilt wird beim Laden, bevor der Vergleichsstand für das Autosave
 * gezogen wird — sonst zählte die Reparatur als Änderung und der Beleg
 * schriebe sich beim blossen Öffnen neu.
 */
export const mitZeilenIds = (draft: WebInvoiceDraft): WebInvoiceDraft => {
  const vergeben = new Set<string>();
  let geaendert = false;
  const zeilen = draft.line_rows.map((line) => {
    const id = String(line.id || "").trim();
    if (id && !vergeben.has(id)) { vergeben.add(id); return line; }
    geaendert = true;
    const neu = neueZeilenId();
    vergeben.add(neu);
    return { ...line, id: neu };
  });
  return geaendert ? { ...draft, line_rows: zeilen } : draft;
};

/**
 * Einen gespeicherten Entwurf auf den heutigen Feldbestand bringen.
 *
 * Ein Entwurf ist ein JSON-Schnappschuss von dem Tag, an dem er entstand —
 * Felder, die es damals nicht gab, fehlen darin schlicht. `draft.doc_title`
 * war dann `undefined`, und das erste `.trim()` darauf legte den ganzen
 * Editor lahm (07.09.2026, weisse Seite). Deshalb wird hier aufgefüllt statt
 * an jeder Lesestelle einzeln abgesichert: eine Stelle, die man beim
 * nächsten neuen Feld wiederfindet.
 */
export const normalisiereDraft = (draft: WebInvoiceDraft): WebInvoiceDraft => {
  const mitIds = mitZeilenIds(draft);
  if (typeof mitIds.doc_title === "string") return mitIds;
  // Altbestand: der Titel entstand aus Belegart und Betreff — genau so, wie
  // ihn die Vorlage bis heute zusammengesetzt hat.
  return { ...mitIds, doc_title: titelVorschlag(mitIds.kind, mitIds.subject || "") };
};

const defaultSnippet = (blocks: TextSnippet[], label: string) => (
  blocks.find((block) => block.label.trim() === label.trim())?.text.trim() || ""
);

/**
 * Standard-Kopf- und -Fußtext einer Belegart aus den Textbausteinen.
 *
 * Wichtig: auch Rechnungen bekommen ihren Fußtext (`notes_invoice`). Vorher
 * blieb er leer — jede Web-Rechnung ging damit ohne den Zahlungshinweis raus,
 * den derselbe Beleg aus der Desktop-App trägt.
 */
const defaultTexts = (kind: InvoiceKind, options?: InvoiceEditorOptions) => {
  const isQuote = kind === "quote";
  const snippets = options?.snippets;
  return {
    intro: isQuote
      ? defaultSnippet(snippets?.intro_quote || [], snippets?.defaults.intro_quote_label || "")
      : defaultSnippet(snippets?.intro_invoice || [], snippets?.defaults.intro_invoice_label || ""),
    notes: isQuote
      ? defaultSnippet(snippets?.notes_quote || [], snippets?.defaults.notes_quote_label || "")
      : defaultSnippet(snippets?.notes_invoice || [], snippets?.defaults.notes_invoice_label || ""),
  };
};

export const createInvoiceDraft = (
  kind: InvoiceKind,
  number: string,
  paymentDays: number,
  options?: InvoiceEditorOptions,
): WebInvoiceDraft => {
  const today = localIso();
  const isQuote = kind === "quote";
  const dueDate = isQuote ? null : addDays(today, paymentDays);
  const termDays = isQuote ? null : paymentDays;
  const { intro, notes } = defaultTexts(kind, options);
  return {
    kind,
    doc_number: number,
    doc_title: titelVorschlag(kind, ""),
    subject: "",
    discount_percent: null,
    discount_amount: null,
    discount_label: "",
    issue_date: today,
    service_date: isQuote ? null : today,
    due_date: dueDate,
    validity_until: isQuote ? addDays(today, 30) : null,
    payment_term_days: termDays,
    source_quote_number: "",
    customer_lines: "",
    customer_number: "",
    intro_paragraphs: applySnippetPlaceholders(intro, today, dueDate, termDays),
    notes_after_table: applySnippetPlaceholders(notes, today, dueDate, termDays),
    payment_reference: "",
    show_tax_quote: true,
    line_rows: [newInvoiceLine()],
    source_project_id: "",
    zugferd: !isQuote,
    xml_out: !isQuote,
    validate_xsd: false,
    abschlag_absetzungen: [],
    interne_kalk: null,
  };
};

/**
 * Kopf- und Fußtext beim Wechsel der Belegart.
 *
 * Es gibt genau zwei Textwelten: Angebot (`intro_quote` / `notes_quote`) und
 * Rechnung (`intro_invoice` / `notes_invoice`); alle drei Rechnungsarten teilen
 * sich die zweite. Daraus folgt die Regel:
 *
 * - **Über die Grenze** (Angebot ⇄ Rechnungsart) wird der Text IMMER auf den
 *   Standard der Zielart gesetzt — auch selbst geschriebener. Auf einer
 *   Rechnung hat Angebotssprache nichts zu suchen (Benjamin, 07.09.2026:
 *   „bei Rechnungen generell kein Textbaustein aus Angeboten"). Vorher blieb
 *   jeder Text stehen, der nicht wörtlich der Standardbaustein war — so trug
 *   Volkers Schlussrechnung „Wir freuen uns auf Ihre Auftragserteilung".
 * - **Innerhalb der Rechnungsarten** (Rechnung ⇄ Abschlag ⇄ Schluss) bleibt der
 *   Text unangetastet. Die Quelle ist dieselbe; jedes Umstellen würde sonst
 *   eine mühsam formulierte Zahlungsvereinbarung wegwerfen.
 *
 * Ein leeres Feld gilt immer als „darf gefüllt werden".
 */
export const retextForKind = (
  draft: WebInvoiceDraft,
  nextKind: InvoiceKind,
  options?: InvoiceEditorOptions,
): Pick<WebInvoiceDraft, "intro_paragraphs" | "notes_after_table" | "doc_title"> => {
  const grenzwechsel = (draft.kind === "quote") !== (nextKind === "quote");
  const after = defaultTexts(nextKind, options);
  const dueDate = nextKind === "quote" ? null : draft.due_date;
  const termDays = nextKind === "quote" ? null : draft.payment_term_days;
  const neuerText = (vorlage: string) => applySnippetPlaceholders(
    vorlage, draft.issue_date, dueDate, termDays,
  );
  const uebernehmen = (aktuell: string, vorlage: string) => (
    grenzwechsel || !aktuell.trim() ? neuerText(vorlage) : aktuell
  );
  // Der Titel trägt die Belegart mit („Angebot Trockenmauer"). Beginnt er mit
  // einer bekannten Belegart, gilt er als Vorschlag: dann wird nur diese
  // ausgetauscht und der Rest bleibt. Hat der Nutzer etwas Eigenes formuliert
  // („Reparatur der Trockenmauer"), bleibt der Titel unangetastet — sonst wäre
  // jede eigene Formulierung beim Umstellen der Belegart weg.
  const titelJetzt = (draft.doc_title || "").trim();
  const folgtDemVorschlag = KIND_LABELS.some(
    (label) => titelJetzt.toLowerCase().startsWith(label.toLowerCase()),
  );
  return {
    intro_paragraphs: uebernehmen(draft.intro_paragraphs, after.intro),
    notes_after_table: uebernehmen(draft.notes_after_table, after.notes),
    doc_title: folgtDemVorschlag || !titelJetzt
      ? titelVorschlag(nextKind, projektTeil(titelJetzt))
      : draft.doc_title,
  };
};

export const customerLines = (customer: Kunde) => {
  const invoiceStreet = customer.rechnung_strasse?.trim();
  const invoicePostcode = customer.rechnung_plz?.trim();
  const invoiceCity = customer.rechnung_ort?.trim();
  const street = invoiceStreet || customer.strasse?.trim();
  const postcode = invoicePostcode || customer.plz?.trim();
  const city = invoiceCity || customer.ort?.trim();
  const structured = [street, `${postcode || ""} ${city || ""}`.trim()].filter(Boolean);
  const fallback = (customer.rechnungsadresse || customer.lieferadresse || "")
    .replace(/\r\n/g, "\n").split("\n").map((line) => line.trim()).filter(Boolean);
  return [customer.name.trim(), ...(structured.length ? structured : fallback)].filter(Boolean).join("\n");
};

/**
 * Rechnungsanschrift in Einzelfelder zerlegen.
 *
 * Das Dokument transportiert die Anschrift als mehrzeiligen Text (so erwartet
 * sie die PDF-Engine). In der Maske ist ein Textfeld aber die falsche Form:
 * niemand sieht, ob Straße oder Ort fehlt, und aus dem Kunden übernommene
 * Adressen lassen sich nicht mit ihm abgleichen. Deshalb hier hin und zurück.
 *
 * Die PLZ-Zeile wird über das führende Postleitzahlmuster erkannt, nicht über
 * ihre Position — bei manchen Adressen steht noch eine Zusatzzeile dazwischen.
 */
export type Anschrift = { name: string; strasse: string; plz: string; ort: string };

export const splitCustomerLines = (text: string): Anschrift => {
  const zeilen = (text || "").replace(/\r\n/g, "\n").split("\n").map((z) => z.trim()).filter(Boolean);
  const name = zeilen[0] || "";
  const rest = zeilen.slice(1);
  const plzIndex = rest.findIndex((z) => /^\d{4,5}\s+\S/.test(z));
  if (plzIndex < 0) return { name, strasse: rest.join(", "), plz: "", ort: "" };
  const treffer = rest[plzIndex].match(/^(\d{4,5})\s+(.*)$/);
  return {
    name,
    strasse: rest.filter((_, i) => i !== plzIndex).join(", "),
    plz: treffer?.[1] || "",
    ort: treffer?.[2] || "",
  };
};

export const joinCustomerLines = (a: Anschrift): string => (
  [a.name, a.strasse, `${a.plz} ${a.ort}`.trim()].map((z) => z.trim()).filter(Boolean).join("\n")
);

/** Anschrift eines Kunden in Feldform — Rechnungsadresse schlägt Hausadresse. */
export const anschriftAusKunde = (kunde: Kunde): Anschrift => ({
  name: (kunde.name || "").trim(),
  strasse: (kunde.rechnung_strasse || kunde.strasse || "").trim(),
  plz: (kunde.rechnung_plz || kunde.plz || "").trim(),
  ort: (kunde.rechnung_ort || kunde.ort || "").trim(),
});

const moneyRound = (value: number) => Math.round((value + Number.EPSILON) * 100) / 100;

export const computeWebDraftTotals = (draft: WebInvoiceDraft) => {
  const byRate = new Map<number, number>();
  let net = 0;
  let optional = 0;
  for (const line of draft.line_rows) {
    const lineNet = moneyRound((Number(line.quantity) || 0) * (Number(line.unit_price_net) || 0));
    // Optionale Positionen laufen in einer eigenen Summe mit: sie gehen in
    // Netto, USt und Gesamt NICHT ein (nicht beauftragt), werden aber daneben
    // ausgewiesen — genauso wie im PDF (`totals.optional_net`).
    if (line.line_kind === "optional") {
      optional = moneyRound(optional + lineNet);
      continue;
    }
    if (line.line_kind !== "position") continue;
    net = moneyRound(net + lineNet);
    byRate.set(line.vat_rate_percent, moneyRound((byRate.get(line.vat_rate_percent) || 0) + lineNet));
  }
  const discount = draft.discount_percent
    ? Math.min(net, moneyRound(net * draft.discount_percent / 100))
    : Math.min(net, moneyRound(draft.discount_amount || 0));
  const netAfterDiscount = moneyRound(net - discount);
  const rates = [...byRate.entries()].sort(([a], [b]) => a - b);
  let discountLeft = discount;
  let vat = 0;
  rates.forEach(([rate, rateNet], index) => {
    const rateDiscount = index === rates.length - 1
      ? discountLeft
      : moneyRound(net > 0 ? discount * rateNet / net : 0);
    discountLeft = moneyRound(discountLeft - rateDiscount);
    vat = moneyRound(vat + moneyRound((rateNet - rateDiscount) * rate / 100));
  });
  return {
    netBeforeDiscount: net,
    discount,
    net: netAfterDiscount,
    vat,
    gross: moneyRound(netAfterDiscount + vat),
    optional,
  };
};

export const applySnippetPlaceholders = (
  text: string,
  issueDate: string,
  dueDate: string | null,
  paymentDays: number | null,
) => {
  const format = (iso: string | null) => {
    if (!iso) return "—";
    const [year, month, day] = iso.split("-");
    return `${day}.${month}.${year}`;
  };
  const days = paymentDays == null ? "—" : String(paymentDays);
  const dayWord = paymentDays == null ? "—" : paymentDays === 1 ? "1 Tag" : `${paymentDays} Tage`;
  return text
    .replaceAll("{{FAELLIGKEIT_DE}}", format(dueDate || issueDate))
    .replaceAll("{{RECHNUNGSDATUM_DE}}", format(issueDate))
    .replaceAll("{{AUSSTELLUNGSDATUM_DE}}", format(issueDate))
    .replaceAll("{{ZAHLUNGSZIEL_TAGE}}", days)
    .replaceAll("{{ZAHLUNGSZIEL_TAGE_WORT}}", dayWord);
};

/** Summe der abgesetzten Abschläge — brutto, weil so auch das PDF rechnet. */
export const absetzungenSumme = (absetzungen: AbschlagAbsetzung[]) => (
  moneyRound(absetzungen.reduce((sum, item) => sum + (Number(item.brutto) || 0), 0))
);

/**
 * Dieselben Pflichtprüfungen, die das Backend vor Vorschau und Festschreiben
 * fährt — nur schon im Browser, damit der Fehler am Feld steht statt als
 * roter Balken nach einem Serverlauf.
 */
export const draftProblems = (draft: WebInvoiceDraft): string[] => {
  const problems: string[] = [];
  if (!draft.customer_number.trim()) problems.push("Bitte einen Kunden aus der Trefferliste auswählen.");
  if (draft.customer_lines.split("\n").filter((line) => line.trim()).length < 2) {
    problems.push("Die Rechnungsanschrift braucht mindestens Name und Ort.");
  }
  const positions = draft.line_rows.filter((line) => line.line_kind === "position");
  if (!positions.length) problems.push("Mindestens eine berechnete Position ist erforderlich.");
  positions.forEach((line, index) => {
    if (!(line.title || line.description).trim()) problems.push(`Position ${index + 1} hat keine Bezeichnung.`);
    if (Math.abs(Number(line.quantity) || 0) < 0.000001) problems.push(`Position ${index + 1} hat keine gültige Menge.`);
  });
  // Ohne Projekt landet der Beleg nirgends: Auftragswert, Wirtschaftlichkeit
  // und „was ist an diesem Garten offen" hängen alle daran. Nachträglich
  // zuordnen ging zwar, ist aber 35 Rechnungen über 63.287 € lang nicht
  // passiert (Befund 05.08.2026). Dieselbe Prüfung steht im Server
  // (`_validate_for_document`) — ändert sich die eine, muss die andere mit.
  if (!draft.source_project_id.trim()) problems.push("Bitte ein Projekt zuordnen.");
  if (draft.kind === "quote" && !draft.validity_until) problems.push("Bitte die Gültigkeit des Angebots festlegen.");
  if (draft.kind !== "quote" && !draft.due_date) problems.push("Bitte die Fälligkeit der Rechnung festlegen.");
  return problems;
};

/** Summe der vier geschätzten Kostenblöcke — Grundlage des Deckungsbeitrags. */
export const kalkSumme = (kalk?: InterneKalkulation | null) => (
  kalk ? moneyRound(kalk.materialkosten + kalk.lohn_helfer + kalk.lohn_inhaber + kalk.fahrt_sonstiges) : 0
);
